import { createScenarioRequest, scenarioRequestDataFromFormData } from "./scenarioRequests.js"

export function customSelectMenuPlacement({
  triggerTop,
  triggerBottom,
  menuHeight,
  viewportHeight,
  containerTop,
  containerBottom,
  preferredPlacement = "auto",
  viewportPadding = 16,
  gap = 8,
  maxMenuHeight = 260,
}) {
  const targetHeight = Math.min(Math.max(Number(menuHeight) || maxMenuHeight, 0), maxMenuHeight)
  const viewportSpaceBelow = Math.max(0, Number(viewportHeight || 0) - Number(triggerBottom || 0) - viewportPadding - gap)
  const viewportSpaceAbove = Math.max(0, Number(triggerTop || 0) - viewportPadding - gap)
  const containerSpaceBelow = containerBottom == null ? Infinity : Math.max(0, Number(containerBottom) - Number(triggerBottom || 0) - gap)
  const containerSpaceAbove = containerTop == null ? Infinity : Math.max(0, Number(triggerTop || 0) - Number(containerTop) - gap)
  const spaceBelow = Math.min(viewportSpaceBelow, containerSpaceBelow)
  const spaceAbove = Math.min(viewportSpaceAbove, containerSpaceAbove)
  const upward = preferredPlacement === "upward" || (spaceBelow < targetHeight && spaceAbove > spaceBelow)
  const available = upward ? spaceAbove : spaceBelow

  return {
    upward,
    maxHeight: available > 0 ? Math.min(targetHeight, available) : targetHeight,
  }
}

// 原官网脚本的 React 初始化封装。内部业务逻辑保持静态包原样，只在外层收集副作用用于路由切换清理。
function createLegacyScope() {
  const listeners = []
  const intervals = []
  const timeouts = []
  const observers = []
  const originalAddEventListener = EventTarget.prototype.addEventListener
  const originalRemoveEventListener = EventTarget.prototype.removeEventListener
  const originalSetInterval = window.setInterval
  const originalSetTimeout = window.setTimeout
  const originalIntersectionObserver = window.IntersectionObserver

  return {
    install() {
      EventTarget.prototype.addEventListener = function trackedAddEventListener(type, listener, options) {
        listeners.push([this, type, listener, options])
        return originalAddEventListener.call(this, type, listener, options)
      }

      EventTarget.prototype.removeEventListener = function trackedRemoveEventListener(type, listener, options) {
        return originalRemoveEventListener.call(this, type, listener, options)
      }

      window.setInterval = function trackedSetInterval(...args) {
        const id = originalSetInterval.apply(window, args)
        intervals.push(id)
        return id
      }

      window.setTimeout = function trackedSetTimeout(...args) {
        const id = originalSetTimeout.apply(window, args)
        timeouts.push(id)
        return id
      }

      if (originalIntersectionObserver) {
        window.IntersectionObserver = class TrackedIntersectionObserver extends originalIntersectionObserver {
          constructor(...args) {
            super(...args)
            observers.push(this)
          }
        }
      }
    },

    restore() {
      EventTarget.prototype.addEventListener = originalAddEventListener
      EventTarget.prototype.removeEventListener = originalRemoveEventListener
      window.setInterval = originalSetInterval
      window.setTimeout = originalSetTimeout
      if (originalIntersectionObserver) window.IntersectionObserver = originalIntersectionObserver
    },

    cleanup() {
      listeners.splice(0).forEach(([target, type, listener, options]) => {
        originalRemoveEventListener.call(target, type, listener, options)
      })
      intervals.splice(0).forEach((id) => window.clearInterval(id))
      timeouts.splice(0).forEach((id) => window.clearTimeout(id))
      observers.splice(0).forEach((observer) => observer.disconnect())
      if (window.setActiveFault) delete window.setActiveFault
      document.body.classList.remove("nav-open", "lightbox-open")
    },
  }
}

export function initRiseConvPage() {
  const scope = createLegacyScope()
  scope.install()

  try {
    const header = document.querySelector("[data-header]");
    const navToggle = document.querySelector("[data-nav-toggle]");
    const navLinks = Array.from(document.querySelectorAll(".site-nav a"));
    const sections = Array.from(document.querySelectorAll(".observe-section"));
    const isCaseDetailPage = document.body.classList.contains("case-detail-page");
    const samePageNavLinks = navLinks.filter((link) => link.getAttribute("href")?.startsWith("#"));
    const isHomePage = !isCaseDetailPage;
    
    const caseData = {
      benz: {
        index: "CASE 01",
        sector: "广告运营",
        url: "/cases/benz-dashboard",
        image: "/riseconv/assets/02-广告AI-奔驰跨平台主题中枢-AMG看板.png",
        alt: "奔驰官方账号 AI 数据看板界面",
        frame: "wide",
        kicker: "广告运营 / 多平台复盘",
        title: "奔驰官方账号 AI 数据看板",
        summary: "把多平台账号数据、车型标签、评论反馈和复盘摘要接到同一套分析流程里。",
        chain: ["权限与来源", "统一标签口径", "评论 AI 分析", "复盘入口"],
        problem: "数据、标签、评论和复盘材料分散。",
        work: "统一权限、口径和评论分析链路。",
        result: "留下可持续复盘的业务入口。",
        materials: "素材：AMG 主题看板、方案长图、权限采集流程。"
      },
      law: {
        index: "CASE 02",
        sector: "法律服务",
        url: "/cases/legal-ai",
        image: "/riseconv/assets/11-法律AI检索-劳动合同经济补偿金检索摘要.png",
        alt: "法律 AI 检索摘要界面",
        frame: "standard",
        kicker: "法律服务 / 可复核输出",
        title: "某律所|AI数据服务",
        summary: "把案件材料检索、法条核查、文本分析和阶段性输出接进可复核工作环境。",
        chain: ["材料入库", "法条核查", "依据留痕", "律师复核"],
        problem: "材料、法条和协作过程分散。",
        work: "补上检索、留痕和律师复核路径。",
        result: "留下可核查的法律 AI 工作环境。",
        materials: "素材：检索摘要界面、阶段性 demo 视频，需确认无敏感案件信息。"
      },
      farm: {
        index: "CASE 03",
        sector: "一线协作",
        url: "/cases/pig-ops",
        image: "/riseconv/assets/18-案例-猪拍卖小程序与后台数据看板-组合展示图.png",
        alt: "猪拍卖小程序与后台数据看板组合展示图",
        frame: "standard",
        kicker: "一线协作 / 数据沉淀",
        title: "大型养殖企业|数据与协作系统",
        summary: "把报价、货源、交易、销售和成本记录放进同一套流程，先让一线业务愿意用起来。",
        chain: ["一线入口", "货源发布", "报价成交", "数据沉淀"],
        problem: "货源、报价、成交和成本靠人工流转。",
        work: "先做一线愿意用的协作入口。",
        result: "沉淀后续分析需要的真实数据。",
        materials: "素材：发布货源表单、个人中心等截图；公开前需继续脱敏。"
      },
      expert: {
        index: "CASE 04",
        sector: "专家服务",
        url: "/cases/expert-qa-platform",
        image: "/riseconv/assets/12-案例-高博士建商执照-首页.jpg",
        alt: "个人专家混合 AI 问答平台首页",
        frame: "wide",
        kicker: "专家服务 / 可运营入口",
        title: "个人专家|混合AI问答平台",
        summary: "把个人专家的专业知识、数据能力和在线服务流程，做成可提问、可付费、可分成的运营入口。",
        chain: ["专业知识入口", "在线提问付费", "专家答复流程", "服务持续运营"],
        problem: "提问、收费、答复和后续服务难规模化。",
        work: "补上支付、答复、后台管理闭环。",
        result: "形成可运营的混合 AI 问答入口。",
        materials: "素材：专家问答平台首页截图；如公开 demo，需确认课程、价格和用户信息边界。"
      },
      security: {
        index: "CASE 05",
        sector: "安全数据",
        url: "/cases/secure-arena",
        image: "/riseconv/assets/11-安全竞赛平台-EVHome工作台界面.jpg",
        alt: "安全数据科学竞赛平台 EV Home 工作台界面",
        frame: "wide",
        kicker: "安全数据 / 可用不可得",
        title: "安全数据|科学竞赛平台",
        summary: "让参赛者在平台内完成建模训练，同时尽量把原始数据留在受控环境内。",
        chain: ["风险入口识别", "前台裁剪", "执行拦截", "审计追踪"],
        problem: "真实数据要可用，但不能被带走。",
        work: "裁剪高风险入口，补上执行拦截和审计。",
        result: "留下可用不可得的竞赛环境。",
        materials: "素材：JupyterLab 风险入口、前台安全改造、Server 层审计方案。"
      }
    };
    
    const caseMobileCopy = {
      benz: {
        badge: "广告数据洞察",
        title: "奔驰官方账号数据看板",
        summary: "把多平台账号数据、车型主题标签、评论反馈和复盘报告放进同一套分析流程，让广告团队不再从导数和拼表开始。"
      },
      law: {
        badge: "专业服务工作台",
        title: "某律所 AI 数据服务",
        summary: "把法条检索、案件材料、来源引用、权限和复核路径放在同一个工作环境中，让 AI 输出可以被专业人员检查。"
      },
      farm: {
        badge: "一线协作系统",
        title: "大型养殖企业 数据与协作系统",
        summary: "把货源发布、报价成交、销售记录和成本数据接进一线愿意用的入口，先让真实数据持续沉淀下来。"
      },
      expert: {
        badge: "专家知识运营",
        title: "个人专家 混合AI问答平台",
        summary: "把专家知识、在线提问、付费答复和后台管理接成可运营的服务入口，让个人专业能力可以持续交付。"
      },
      security: {
        badge: "安全训练环境",
        title: "安全数据科学竞赛平台",
        summary: "让参赛者在受控平台内完成训练和建模，同时把原始数据、执行入口和审计链路留在安全边界里。"
      }
    };
    
    const smartTitleSelector = [
      "h1",
      "h2",
      "h3",
      "[data-case-title]",
      "[data-diagnosis-title]"
    ].join(",");
    const smartTitleSkipSelector = ".brand, .site-nav, .service-stack, [data-smart-skip], [data-fault-diagnosis-title]";
    let smartTitleMeasure;
    let smartTitleResizeTimer;
    
    function normalizeTitleText(text) {
      return text?.toString().replace(/\s+/g, " ").trim() || "";
    }
    
    function ensureSmartTitleMeasure() {
      if (smartTitleMeasure) return smartTitleMeasure;
    
      smartTitleMeasure = document.createElement("span");
      smartTitleMeasure.setAttribute("aria-hidden", "true");
      smartTitleMeasure.style.cssText = [
        "position:absolute",
        "left:-9999px",
        "top:-9999px",
        "visibility:hidden",
        "white-space:nowrap",
        "pointer-events:none"
      ].join(";");
      document.body.append(smartTitleMeasure);
      return smartTitleMeasure;
    }
    
    function measureTitleLine(element, text) {
      const measure = ensureSmartTitleMeasure();
      const style = window.getComputedStyle(element);
      measure.style.font = style.font;
      measure.style.letterSpacing = style.letterSpacing;
      measure.textContent = text;
      return measure.getBoundingClientRect().width;
    }
    
    function getTitleBreakCandidates(text) {
      const candidates = new Set();
      const punctuation = /[，。；：、,]/g;
      const spaces = /\s+/g;
      let match;
    
      while ((match = punctuation.exec(text))) {
        candidates.add(match.index + 1);
      }
    
      while ((match = spaces.exec(text))) {
        candidates.add(match.index + match[0].length);
      }
    
      if (window.Intl?.Segmenter) {
        let offset = 0;
        const segmenter = new Intl.Segmenter("zh-CN", { granularity: "word" });
    
        Array.from(segmenter.segment(text)).forEach(({ segment }) => {
          offset += segment.length;
          candidates.add(offset);
        });
      }
    
      [
        "而是",
        "不是",
        "能不能",
        "要不要",
        "值不值得",
        "可以",
        "通常",
        "真正",
        "可用",
        "最后",
        "每个",
        "再",
        "把",
        "让",
        "从",
        "接成",
        "带进",
        "变成",
        "扩成",
        "做成",
        "拆成",
        "沉淀成",
        "翻译成",
        "补上",
        "直接",
        "业务",
        "人工",
        "流转",
        "地方",
        "系统",
        "平台",
        "产品化",
        "知识",
        "资产",
        "数据",
        "沉淀",
        "沉淀不了",
        "安全",
        "受控",
        "科学",
        "竞赛",
        "数据科学",
        "竞赛平台",
        "资料边界",
        "权限体系",
        "检索核查",
        "专家服务",
        "工程能力",
        "业务现场",
        "业务系统",
        "关键工作",
        "工作流",
        "落地",
        "和",
        "与"
      ].forEach((word) => {
        let index = text.indexOf(word);
        while (index !== -1) {
          candidates.add(index);
          index = text.indexOf(word, index + word.length);
        }
      });
    
      return Array.from(candidates)
        .filter((index) => index >= 2 && text.length - index >= 2)
        .filter((index) => !/[，。；：、,]/.test(text[index] || ""))
        .sort((a, b) => a - b);
    }
    
    function pushTitleBreakPlans(candidates, maxBreaks, onPlan) {
      const build = (startIndex, plan) => {
        if (plan.length) onPlan(plan);
        if (plan.length >= maxBreaks) return;
    
        for (let index = startIndex; index < candidates.length; index += 1) {
          build(index + 1, [...plan, candidates[index]]);
        }
      };
    
      build(0, []);
    }
    
    function splitTitleByBreaks(text, breaks) {
      const lines = [];
      let start = 0;
    
      breaks.forEach((breakPoint) => {
        lines.push(text.slice(start, breakPoint).trim());
        start = breakPoint;
      });
    
      lines.push(text.slice(start).trim());
      return lines.filter(Boolean);
    }
    
    function scoreTitleLines(element, lines, availableWidth) {
      const widths = lines.map((line) => measureTitleLine(element, line));
      const maxWidth = Math.max(...widths);
    
      if (maxWidth > availableWidth + 1) return Number.POSITIVE_INFINITY;
      if (lines.some((line) => /^[，。；：、,]/.test(line))) return Number.POSITIVE_INFINITY;
    
      const average = widths.reduce((sum, width) => sum + width, 0) / widths.length;
      const balance = widths.reduce((sum, width) => sum + Math.abs(width - average), 0) / availableWidth;
      const orphanPenalty = lines.some((line) => line.length < 4) ? 2 : 0;
      const danglingEndPenalty = lines.slice(0, -1).filter((line) => /[能是把让从和与在接这的多件地要还]$/.test(line)).length * 2.4;
      const weakStartPenalty = lines.slice(1).filter((line) => /^(的|成|件|事|方|交付|账号|功能|不要|靠)/.test(line)).length * 1.8;
      const fullStopPenalty = lines.slice(0, -1).filter((line) => /。$/.test(line)).length * 0.6;
      const lineCountPenalty = Math.max(0, lines.length - 2) * 0.72;
    
      return maxWidth / availableWidth + balance + orphanPenalty + danglingEndPenalty + weakStartPenalty + fullStopPenalty + lineCountPenalty;
    }
    
    function chooseSmartTitleLines(element, text, explicitLines, availableWidth) {
      if (measureTitleLine(element, text) <= availableWidth + 1) return [text];
    
      const candidatePlans = [];
    
      if (explicitLines.length > 1) {
        candidatePlans.push(explicitLines);
      }
    
      const candidates = getTitleBreakCandidates(text);
      const maxLines = availableWidth < 320 && text.length > 24 ? 6 : availableWidth < 360 ? 5 : 4;
    
      pushTitleBreakPlans(candidates, maxLines - 1, (breaks) => {
        candidatePlans.push(splitTitleByBreaks(text, breaks));
      });
    
      let bestLines = explicitLines.length > 1 ? explicitLines : [text];
      let bestScore = Number.POSITIVE_INFINITY;
    
      candidatePlans.forEach((lines) => {
        if (lines.length < 2 || lines.length > maxLines) return;
        const score = scoreTitleLines(element, lines, availableWidth);
        if (score < bestScore) {
          bestScore = score;
          bestLines = lines;
        }
      });
    
      return bestLines;
    }
    
    function applySmartTitleLines(element, lines) {
      element.classList.toggle("is-smart-title", lines.length > 1);
    
      if (lines.length === 1) {
        element.textContent = lines[0];
        return;
      }
    
      element.replaceChildren();
      lines.forEach((line) => {
        const lineElement = document.createElement("span");
        lineElement.className = "smart-title-line";
        lineElement.textContent = line;
        element.append(lineElement);
      });
    }
    
    function smartWrapTitle(element, sourceTitle) {
      if (!element) return;
    
      const availableWidth = element.clientWidth || element.getBoundingClientRect().width;
      const baseTitleSource = normalizeTitleText(sourceTitle || element.dataset.smartTitleBase || element.dataset.smartTitleSource || element.textContent);
      if (!baseTitleSource) return;
    
      if (sourceTitle || !element.dataset.smartTitleBase) {
        element.dataset.smartTitleBase = baseTitleSource;
      }
    
      const mobileTitleSource = element.dataset.smartTitleMobileSource;
      const shouldUseMobileTitle = Boolean(mobileTitleSource && window.matchMedia("(max-width: 760px)").matches);
      const rawTitle = normalizeTitleText(shouldUseMobileTitle ? mobileTitleSource : baseTitleSource);
      if (!rawTitle) return;
    
      const widthKey = String(Math.round(availableWidth || 0));
      if (availableWidth && !sourceTitle && element.dataset.smartTitleLastWidth === widthKey && element.dataset.smartTitleLastRaw === rawTitle) return;
    
      element.dataset.smartTitleSource = baseTitleSource;
      if (availableWidth) {
        element.dataset.smartTitleLastWidth = widthKey;
        element.dataset.smartTitleLastRaw = rawTitle;
      }
      const explicitLines = rawTitle.split("|").map((line) => normalizeTitleText(line)).filter(Boolean);
      const titleText = explicitLines.join("");
    
      if (explicitLines.length > 1) {
        const explicitLinesFit = availableWidth
          ? explicitLines.every((line) => measureTitleLine(element, line) <= availableWidth + 1)
          : true;
    
        if (!explicitLinesFit) {
          const lines = chooseSmartTitleLines(element, titleText, [], availableWidth);
          applySmartTitleLines(element, lines);
          return;
        }
    
        applySmartTitleLines(element, explicitLines);
        return;
      }
    
      applySmartTitleLines(element, [titleText]);
    
      if (!availableWidth) return;
    
      const lines = chooseSmartTitleLines(element, titleText, explicitLines, availableWidth);
      applySmartTitleLines(element, lines);
    }
    
    function smartWrapAllTitles() {
      const titleSelector = isCaseDetailPage
        ? ".case-detail-label h2, [data-case-title], [data-diagnosis-title]"
        : smartTitleSelector;
    
      document.querySelectorAll(titleSelector).forEach((title) => {
        if (title.closest(smartTitleSkipSelector)) return;
        smartWrapTitle(title);
      });
    }
    
    function scheduleSmartTitleWrap() {
      window.clearTimeout(smartTitleResizeTimer);
      smartTitleResizeTimer = window.setTimeout(refreshSmartTitles, 120);
    }
    
    const setHeader = () => {
      header?.classList.toggle("is-scrolled", window.scrollY > 24);
    };
    let headerFrame = 0;
    
    setHeader();
    window.addEventListener("scroll", () => {
      if (headerFrame) return;
    
      headerFrame = window.requestAnimationFrame(() => {
        headerFrame = 0;
        setHeader();
      });
    }, { passive: true });
    
    navToggle?.addEventListener("click", () => {
      const isOpen = document.body.classList.toggle("nav-open");
      navToggle.setAttribute("aria-expanded", String(isOpen));
    });
    
    navLinks.forEach((link) => {
      link.addEventListener("click", () => {
        document.body.classList.remove("nav-open");
        navToggle?.setAttribute("aria-expanded", "false");
      });
    });
    
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
    
          entry.target.classList.add("is-visible");
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.16 }
    );
    
    sections.forEach((section) => revealObserver.observe(section));
    
    if (samePageNavLinks.length) {
      const navObserver = new IntersectionObserver(
        (entries) => {
          const active = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
    
          if (!active) return;
    
          samePageNavLinks.forEach((link) => {
            const id = link.getAttribute("href")?.slice(1);
            link.classList.toggle("is-active", id === active.target.id);
          });
        },
        {
          rootMargin: "-35% 0px -50% 0px",
          threshold: [0.1, 0.35, 0.65]
        }
      );
    
      sections.forEach((section) => navObserver.observe(section));
    }
    
    function renderMobileCaseGallery() {
      if (!isHomePage) return;
    
      const casesSection = document.querySelector(".cases");
      const showcase = document.querySelector(".case-showcase");
      if (!casesSection || !showcase || casesSection.querySelector("[data-case-mobile-gallery]")) return;
    
      const gallery = document.createElement("div");
      gallery.className = "case-mobile-gallery";
      gallery.dataset.caseMobileGallery = "";
      gallery.setAttribute("aria-label", "案例卡片");
    
      Object.entries(caseData).forEach(([key, data], index) => {
        const copy = caseMobileCopy[key] || {};
        const card = document.createElement("article");
        card.className = "case-mobile-card";
        card.style.setProperty("--case-card-delay", `${index * 90}ms`);
    
        const visual = document.createElement("button");
        visual.className = "case-mobile-visual";
        visual.type = "button";
        visual.dataset.caseZoomTrigger = "";
        visual.dataset.caseImageSrc = data.image;
        visual.dataset.caseImageAlt = data.alt;
        visual.dataset.caseCaption = copy.title || data.title.replace(/\|/g, " ");
        visual.setAttribute("aria-label", `放大查看${visual.dataset.caseCaption}图片`);
    
        const image = document.createElement("img");
        image.src = data.image;
        image.alt = data.alt;
        image.loading = index > 1 ? "lazy" : "eager";
    
        const indexMark = document.createElement("span");
        indexMark.className = "case-mobile-index";
        indexMark.textContent = String(index + 1).padStart(2, "0");
    
        visual.append(image, indexMark);
    
        const copyWrap = document.createElement("div");
        copyWrap.className = "case-mobile-copy";
    
        const badge = document.createElement("p");
        badge.className = "case-mobile-badge";
        badge.textContent = copy.badge || data.sector;
    
        const title = document.createElement("h3");
        title.textContent = copy.title || data.title.replace(/\|/g, " ");
    
        const summary = document.createElement("p");
        summary.className = "case-mobile-summary";
        summary.textContent = copy.summary || data.summary;
    
        const link = document.createElement("a");
        link.className = "case-mobile-link";
        link.href = data.url;
        link.innerHTML = '阅读案例 <span aria-hidden="true">→</span>';
    
        copyWrap.append(badge, title, summary, link);
        card.append(visual, copyWrap);
        gallery.append(card);
      });
    
      showcase.insertAdjacentElement("afterend", gallery);
      casesSection.classList.add("has-mobile-case-gallery");
    }
    
    renderMobileCaseGallery();
    
    const caseSwitches = isHomePage ? Array.from(document.querySelectorAll("[data-case]")) : [];
    const caseShowcase = isHomePage ? document.querySelector(".case-showcase") : null;
    const caseFeature = isHomePage ? document.querySelector(".case-feature") : null;
    const caseImage = isHomePage ? document.querySelector("[data-case-image]") : null;
    const caseImageFrame = isHomePage ? document.querySelector(".case-image-frame") : null;
    const caseKicker = isHomePage ? document.querySelector("[data-case-kicker]") : null;
    const caseIndex = isHomePage ? document.querySelector("[data-case-index]") : null;
    const caseSector = isHomePage ? document.querySelector("[data-case-sector]") : null;
    const caseTitle = isHomePage ? document.querySelector("[data-case-title]") : null;
    const caseSummary = isHomePage ? document.querySelector("[data-case-summary]") : null;
    const caseChain = isHomePage ? document.querySelector("[data-case-chain]") : null;
    const caseProblem = isHomePage ? document.querySelector("[data-case-problem]") : null;
    const caseWork = isHomePage ? document.querySelector("[data-case-work]") : null;
    const caseResult = isHomePage ? document.querySelector("[data-case-result]") : null;
    const caseMaterials = isHomePage ? document.querySelector("[data-case-materials]") : null;
    const caseLink = isHomePage ? document.querySelector("[data-case-link]") : null;
    const caseLightbox = isHomePage ? document.querySelector("[data-case-lightbox]") : null;
    const caseLightboxImage = isHomePage ? document.querySelector("[data-case-lightbox-image]") : null;
    const caseLightboxCaption = isHomePage ? document.querySelector("[data-case-lightbox-caption]") : null;
    const caseLightboxCloseButtons = isHomePage ? Array.from(document.querySelectorAll("[data-case-lightbox-close]")) : [];
    const caseZoomTriggers = isHomePage ? Array.from(document.querySelectorAll("[data-case-zoom-trigger]")) : [];
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let activeCaseIndex = 0;
    let caseAutoplay;
    let caseLightboxLastFocus = null;
    let caseTransitionTimer;
    
    function createViewportAutoplay(target, start, stop) {
      let isInView = !target;
    
      const sync = () => {
        if (document.hidden || !isInView) {
          stop();
          return;
        }
    
        start();
      };
    
      if (target && "IntersectionObserver" in window) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            isInView = entry.isIntersecting;
            sync();
          },
          { rootMargin: "180px 0px", threshold: 0.01 }
        );
    
        observer.observe(target);
      } else {
        isInView = true;
      }
    
      document.addEventListener("visibilitychange", sync);
    
      return sync;
    }
    
    const snapshotAutoplayGrids = Array.from(document.querySelectorAll("[data-snapshot-autoplay]"));
    
    snapshotAutoplayGrids.forEach((grid) => {
      const cards = Array.from(grid.querySelectorAll(".case-snapshot-card"));
      let activeSnapshotIndex = 0;
      let hasRenderedSnapshot = false;
      let snapshotTimer;
    
      const syncSnapshotCard = (card, cardIndex) => {
        const isActive = cardIndex === activeSnapshotIndex;
        card.classList.toggle("is-active", isActive);
        card.setAttribute("aria-current", isActive ? "step" : "false");
      };
    
      const setActiveSnapshot = (index) => {
        if (!cards.length) return;
        const previousSnapshotIndex = activeSnapshotIndex;
        activeSnapshotIndex = (index + cards.length) % cards.length;
    
        if (!hasRenderedSnapshot) {
          cards.forEach(syncSnapshotCard);
          hasRenderedSnapshot = true;
          return;
        }
    
        if (previousSnapshotIndex === activeSnapshotIndex) return;
        syncSnapshotCard(cards[previousSnapshotIndex], previousSnapshotIndex);
        syncSnapshotCard(cards[activeSnapshotIndex], activeSnapshotIndex);
      };
    
      const stopSnapshotAutoplay = () => {
        window.clearInterval(snapshotTimer);
        grid.classList.add("is-paused");
      };
    
      const startSnapshotAutoplay = () => {
        window.clearInterval(snapshotTimer);
        if (reducedMotion.matches || cards.length < 2 || document.hidden) return;
    
        grid.classList.remove("is-paused");
        snapshotTimer = window.setInterval(() => {
          setActiveSnapshot(activeSnapshotIndex + 1);
        }, 4200);
      };
    
      setActiveSnapshot(0);
      const syncSnapshotAutoplay = createViewportAutoplay(grid, startSnapshotAutoplay, stopSnapshotAutoplay);
      syncSnapshotAutoplay();
    
      grid.addEventListener("mouseenter", stopSnapshotAutoplay);
      grid.addEventListener("mouseleave", startSnapshotAutoplay);
      grid.addEventListener("focusin", stopSnapshotAutoplay);
      grid.addEventListener("focusout", () => {
        window.setTimeout(() => {
          if (!grid.contains(document.activeElement)) startSnapshotAutoplay();
        }, 0);
      });
    
      reducedMotion.addEventListener("change", () => {
        if (reducedMotion.matches) {
          stopSnapshotAutoplay();
          setActiveSnapshot(0);
          return;
        }
    
        syncSnapshotAutoplay();
      });
    });
    
    function setActiveCase(index) {
      if (!caseSwitches.length) return;
    
      activeCaseIndex = (index + caseSwitches.length) % caseSwitches.length;
      const button = caseSwitches[activeCaseIndex];
      const data = caseData[button.dataset.case];
      if (!data) return;
      window.clearTimeout(caseTransitionTimer);
    
      caseSwitches.forEach((item) => {
        const isActive = item === button;
        item.classList.toggle("is-active", isActive);
        item.setAttribute("aria-pressed", String(isActive));
        item.setAttribute("aria-selected", String(isActive));
      });
    
      const applyCaseData = () => {
        caseImage.src = data.image;
        caseImage.alt = data.alt;
        if (caseImageFrame) caseImageFrame.dataset.caseFrame = data.frame || "default";
        caseKicker.textContent = data.kicker;
        caseIndex.textContent = data.index;
        caseSector.textContent = data.sector;
        smartWrapTitle(caseTitle, data.title);
        caseSummary.textContent = data.summary;
        caseChain.innerHTML = data.chain.map((item) => `<span>${item}</span>`).join("");
        caseProblem.textContent = data.problem;
        caseWork.textContent = data.work;
        caseResult.textContent = data.result;
        caseMaterials.textContent = data.materials;
        if (caseLink) caseLink.href = data.url;
    
        if (caseFeature) {
          caseFeature.classList.remove("is-case-exiting", "is-switching");
          void caseFeature.offsetWidth;
          caseFeature.classList.add("is-switching");
        }
      };
    
      if (!caseFeature || reducedMotion.matches) {
        applyCaseData();
        return;
      }
    
      caseFeature.classList.remove("is-switching");
      caseFeature.classList.add("is-case-exiting");
    
      caseTransitionTimer = window.setTimeout(applyCaseData, 120);
    }
    
    function stopCaseAutoplay() {
      window.clearInterval(caseAutoplay);
    }
    
    function startCaseAutoplay() {
      stopCaseAutoplay();
      if (reducedMotion.matches || caseSwitches.length < 2 || caseLightbox?.classList.contains("is-open")) return;
    
      caseAutoplay = window.setInterval(() => {
        setActiveCase(activeCaseIndex + 1);
      }, 4300);
    }
    
    if (caseSwitches.length) {
      caseSwitches.forEach((button, index) => {
        button.addEventListener("click", () => {
          setActiveCase(index);
          startCaseAutoplay();
        });
      });
    
      caseShowcase?.addEventListener("mouseenter", stopCaseAutoplay);
      caseShowcase?.addEventListener("mouseleave", startCaseAutoplay);
      caseShowcase?.addEventListener("focusin", stopCaseAutoplay);
      caseShowcase?.addEventListener("focusout", () => {
        window.setTimeout(() => {
          if (!caseShowcase.contains(document.activeElement)) startCaseAutoplay();
        }, 0);
      });
    
      const syncCaseAutoplay = createViewportAutoplay(caseShowcase, startCaseAutoplay, stopCaseAutoplay);
    
      reducedMotion.addEventListener("change", syncCaseAutoplay);
      syncCaseAutoplay();
    }
    
    function openCaseLightbox(trigger = caseImageFrame) {
      if (!caseLightbox || !caseLightboxImage) return;
    
      const sourceImage = trigger?.querySelector?.("img") || caseImage;
      const source = trigger?.dataset?.caseImageSrc || sourceImage?.currentSrc || sourceImage?.src;
      if (!source) return;
    
      caseLightboxLastFocus = document.activeElement;
      caseLightboxImage.src = source;
      caseLightboxImage.alt = trigger?.dataset?.caseImageAlt || sourceImage?.alt || "案例图片放大预览";
    
      if (caseLightboxCaption) {
        caseLightboxCaption.textContent =
          trigger?.dataset?.caseCaption ||
          caseTitle?.textContent.trim() ||
          sourceImage?.alt ||
          "案例图片预览";
      }
    
      caseLightbox.classList.add("is-open");
      caseLightbox.setAttribute("aria-hidden", "false");
      document.body.classList.add("lightbox-open");
      stopCaseAutoplay();
      caseLightbox.querySelector(".case-lightbox-close")?.focus();
    }
    
    function closeCaseLightbox() {
      if (!caseLightbox) return;
    
      caseLightbox.classList.remove("is-open");
      caseLightbox.setAttribute("aria-hidden", "true");
      document.body.classList.remove("lightbox-open");
    
      if (caseLightboxImage) {
        caseLightboxImage.removeAttribute("src");
        caseLightboxImage.alt = "";
      }
    
      if (caseLightboxLastFocus instanceof HTMLElement) {
        caseLightboxLastFocus.focus();
      }
    
      startCaseAutoplay();
    }
    
    caseZoomTriggers.forEach((trigger) => {
      trigger.addEventListener("click", () => openCaseLightbox(trigger));
      trigger.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        openCaseLightbox(trigger);
      });
    });
    
    caseLightboxCloseButtons.forEach((button) => {
      button.addEventListener("click", closeCaseLightbox);
    });
    
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && caseLightbox?.classList.contains("is-open")) {
        closeCaseLightbox();
      }
    });
    
    const serviceCards = isHomePage ? Array.from(document.querySelectorAll("[data-service-card]")) : [];
    let activeServiceIndex = 0;
    let serviceAutoplay;
    
    const serviceTitleGroups = {
      "企业 AI 业务系统落地": ["企业 AI ", "业务系统落地"],
      "数据与知识资产平台": ["数据与", "知识资产平台"],
      "行业 AI 工作流改造": ["行业 AI ", "工作流改造"],
      "安全受控的数据使用平台": ["安全受控的", "数据使用平台"],
      "专家服务产品化": ["专家服务产品化"]
    };
    
    function splitServiceTitleCharacters(text) {
      const units = [];
      let latinBuffer = "";
    
      Array.from(text).forEach((character) => {
        if (/[A-Za-z0-9]/.test(character)) {
          latinBuffer += character;
          return;
        }
    
        if (latinBuffer) {
          units.push(latinBuffer);
          latinBuffer = "";
        }
    
        units.push(character);
      });
    
      if (latinBuffer) units.push(latinBuffer);
    
      return units;
    }
    
    function getServiceTitleGroups(titleText) {
      return serviceTitleGroups[titleText] || [titleText];
    }
    
    function hydrateServiceTitleCharacters(cards) {
      cards.forEach((card) => {
        const title = card.querySelector("h3");
        if (!title || title.dataset.serviceTitleHydrated === "true") return;
    
        const titleText = title.textContent.replace(/\s+/g, " ").trim();
        if (!titleText) return;
    
        title.dataset.serviceTitleHydrated = "true";
        title.setAttribute("aria-label", titleText);
    
        const fragment = document.createDocumentFragment();
        let characterIndex = 0;
    
        getServiceTitleGroups(titleText).forEach((group) => {
          const groupElement = document.createElement("span");
          groupElement.className = "service-title-unit";
    
          splitServiceTitleCharacters(group).forEach((unit) => {
            const unitElement = document.createElement("span");
            unitElement.className = "service-title-char";
            unitElement.style.setProperty("--service-char-index", characterIndex);
            characterIndex += 1;
    
            if (unit === " ") {
              unitElement.classList.add("is-space");
              unitElement.innerHTML = "&nbsp;";
            } else {
              unitElement.textContent = unit;
            }
    
            groupElement.append(unitElement);
          });
    
          fragment.append(groupElement);
        });
    
        title.replaceChildren(fragment);
      });
    }
    
    function replayServiceTitleAnimation(card) {
      const title = card?.querySelector("h3");
      if (!title || reducedMotion.matches) return;
    
      title.classList.remove("is-title-revealing");
    
      window.requestAnimationFrame(() => {
        if (card.classList.contains("is-expanded")) {
          title.classList.add("is-title-revealing");
        }
      });
    }
    
    function setActiveService(index) {
      if (!serviceCards.length) return;
    
      activeServiceIndex = (index + serviceCards.length) % serviceCards.length;
    
      serviceCards.forEach((card, cardIndex) => {
        const isActive = cardIndex === activeServiceIndex;
        card.classList.toggle("is-expanded", isActive);
        card.setAttribute("aria-pressed", String(isActive));
        card.setAttribute("aria-current", isActive ? "true" : "false");
        if (isActive) {
          replayServiceTitleAnimation(card);
        } else {
          card.querySelector("h3")?.classList.remove("is-title-revealing");
        }
      });
    }
    
    function stopServiceAutoplay() {
      window.clearInterval(serviceAutoplay);
    }
    
    function startServiceAutoplay() {
      stopServiceAutoplay();
    
      if (reducedMotion.matches || serviceCards.length < 2) return;
    
      serviceAutoplay = window.setInterval(() => {
        setActiveService(activeServiceIndex + 1);
      }, 3600);
    }
    
    if (serviceCards.length) {
      hydrateServiceTitleCharacters(serviceCards);
      setActiveService(0);
      const servicesSection = serviceCards[0]?.closest(".services") || serviceCards[0]?.parentElement;
      const syncServiceAutoplay = createViewportAutoplay(servicesSection, startServiceAutoplay, stopServiceAutoplay);
      syncServiceAutoplay();
    
      serviceCards.forEach((card, index) => {
        card.addEventListener("click", () => {
          setActiveService(index);
          startServiceAutoplay();
        });
    
        card.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          setActiveService(index);
          startServiceAutoplay();
        });
      });
    
      reducedMotion.addEventListener("change", syncServiceAutoplay);
    }
    
    const faultBoard = isHomePage ? document.querySelector("[data-fault-board]") : null;
    const faultCards = isHomePage ? Array.from(document.querySelectorAll("[data-fault-card]")) : [];
    const faultEcgLine = isHomePage ? document.querySelector(".fault-ecg-live") : null;
    const faultEcgSegments = [
      "M8 44 h0.01",
      "M8 44 H44 L58 44 L72 24",
      "M8 44 H44 L58 44 L72 24 L88 56 L108 10 L132 44",
      "M8 44 H44 L58 44 L72 24 L88 56 L108 10 L132 44 H164 L178 34 L190 50 L206 44",
      "M8 44 H44 L58 44 L72 24 L88 56 L108 10 L132 44 H164 L178 34 L190 50 L206 44 H238 L250 26 L270 58",
      "M8 44 H44 L58 44 L72 24 L88 56 L108 10 L132 44 H164 L178 34 L190 50 L206 44 H238 L250 26 L270 58 L288 44 H352"
    ];
    const faultDiagnosis = {
      panel: isHomePage ? document.querySelector("[data-fault-diagnosis]") : null,
      title: isHomePage ? document.querySelector("[data-fault-diagnosis-title]") : null,
      copy: isHomePage ? document.querySelector("[data-fault-diagnosis-copy]") : null,
      gap: isHomePage ? document.querySelector("[data-fault-diagnosis-gap]") : null,
      action: isHomePage ? document.querySelector("[data-fault-diagnosis-action]") : null,
      index: isHomePage ? document.querySelector("[data-fault-diagnosis-index]") : null,
      step: isHomePage ? document.querySelector("[data-fault-diagnosis-step]") : null,
      tag: isHomePage ? document.querySelector("[data-fault-diagnosis-tag]") : null,
      progressLabel: isHomePage ? document.querySelector("[data-fault-progress-label]") : null,
    };
    const faultProgressNodes = isHomePage ? Array.from(document.querySelectorAll("[data-fault-progress-node]")) : [];
    let activeFaultIndex = 0;
    let faultAutoplay;
    
    function renderFaultTitle(title) {
      if (!faultDiagnosis.title || !title) return;
    
      const lines = title.split("|").map((line) => line.trim()).filter(Boolean);
      const fullTitle = lines.join("");
    
      const applyTitleLines = (titleLines, mode = "") => {
        faultDiagnosis.title.replaceChildren();
        faultDiagnosis.title.classList.toggle("is-title-compact", mode === "compact");
        faultDiagnosis.title.classList.toggle("is-title-single", titleLines.length === 1);
    
        titleLines.forEach((line) => {
          const lineElement = document.createElement("span");
          lineElement.textContent = line;
          faultDiagnosis.title.append(lineElement);
        });
      };
    
      applyTitleLines([fullTitle]);
    
      const singleLine = faultDiagnosis.title.firstElementChild;
      const availableWidth = faultDiagnosis.title.clientWidth || faultDiagnosis.title.getBoundingClientRect().width;
      const singleLineWidth = singleLine?.scrollWidth || singleLine?.getBoundingClientRect().width || 0;
    
      if (singleLineWidth <= availableWidth + 1) return;
    
      if (lines.length > 1 && singleLineWidth <= availableWidth * 1.14) {
        applyTitleLines([fullTitle], "compact");
        const compactLine = faultDiagnosis.title.firstElementChild;
        const compactLineWidth = compactLine?.scrollWidth || compactLine?.getBoundingClientRect().width || 0;
        if (compactLineWidth <= availableWidth + 1) return;
      }
    
      if (lines.length > 1) {
        applyTitleLines(lines);
      }
    }
    
    function setActiveFault(index) {
      if (!faultCards.length) return;
    
      activeFaultIndex = (index + faultCards.length) % faultCards.length;
      const scanProgress = faultCards.length > 1 ? activeFaultIndex / (faultCards.length - 1) : 0;
      const scanPosition = `${8 + scanProgress * 84}%`;
      const activeEcgSegment = faultEcgSegments[activeFaultIndex] || faultEcgSegments.at(-1);
    
      faultEcgLine?.setAttribute("d", activeEcgSegment);
    
      faultBoard?.style.setProperty("--scan-left", scanPosition);
      faultBoard?.style.setProperty("--scan-top", scanPosition);
      faultBoard?.style.setProperty("--fault-progress", `${(activeFaultIndex + 1) / faultCards.length * 100}%`);
      faultBoard?.style.setProperty("--fault-ratio", scanProgress.toFixed(3));
      faultBoard?.style.setProperty("--fault-dash", activeFaultIndex > 0 ? "100" : "0");
    
      faultCards.forEach((card, cardIndex) => {
        const isActive = cardIndex === activeFaultIndex;
        card.classList.toggle("is-scanned", isActive);
        card.setAttribute("aria-pressed", String(isActive));
        card.setAttribute("aria-current", isActive ? "true" : "false");
      });
    
      faultProgressNodes.forEach((node, nodeIndex) => {
        node.classList.toggle("is-passed", nodeIndex < activeFaultIndex);
        node.classList.toggle("is-current", nodeIndex === activeFaultIndex);
        node.setAttribute("aria-pressed", String(nodeIndex === activeFaultIndex));
        node.setAttribute("aria-current", nodeIndex === activeFaultIndex ? "step" : "false");
      });
    
      const activeCard = faultCards[activeFaultIndex];
      const step = activeCard.querySelector("span")?.textContent.trim();
      const tag = activeCard.querySelector("em")?.textContent.trim();
      const title = activeCard.dataset.panelTitle || activeCard.querySelector("h3")?.textContent.trim();
      const copy = activeCard.querySelector("p:not(.fault-detail)")?.textContent.trim();
      const gap = activeCard.dataset.gap;
      const action = activeCard.dataset.action;
    
      if (faultDiagnosis.step && step) faultDiagnosis.step.textContent = step;
      if (faultDiagnosis.tag && tag) faultDiagnosis.tag.textContent = tag;
      if (faultDiagnosis.progressLabel && tag) faultDiagnosis.progressLabel.textContent = `正在定位：${tag}`;
      renderFaultTitle(title);
      if (faultDiagnosis.copy && copy) faultDiagnosis.copy.textContent = copy;
      if (faultDiagnosis.gap && gap) faultDiagnosis.gap.textContent = gap;
      if (faultDiagnosis.action && action) faultDiagnosis.action.textContent = action;
      if (faultDiagnosis.index) {
        faultDiagnosis.index.textContent = `${String(activeFaultIndex + 1).padStart(2, "0")} / ${String(faultCards.length).padStart(2, "0")}`;
      }
    
      if (faultDiagnosis.panel) {
        faultDiagnosis.panel.classList.remove("is-switching");
        void faultDiagnosis.panel.offsetWidth;
        faultDiagnosis.panel.classList.add("is-switching");
      }
    }
    
    function startFaultAutoplay() {
      stopFaultAutoplay();
    
      if (reducedMotion.matches || faultCards.length < 2) return;
    
      faultAutoplay = window.setInterval(() => {
        setActiveFault(activeFaultIndex + 1);
      }, 3200);
    }
    
    function stopFaultAutoplay() {
      window.clearInterval(faultAutoplay);
    }
    
    if (faultCards.length) {
      setActiveFault(0);
      const syncFaultAutoplay = createViewportAutoplay(faultBoard, startFaultAutoplay, stopFaultAutoplay);
      syncFaultAutoplay();
    
      faultCards.forEach((card, index) => {
        card.addEventListener("click", () => {
          setActiveFault(index);
          startFaultAutoplay();
        });
    
        card.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          setActiveFault(index);
          startFaultAutoplay();
        });
      });
    
      faultProgressNodes.forEach((node, index) => {
        const card = faultCards[index];
        const step = card?.querySelector("span")?.textContent.trim() || String(index + 1).padStart(2, "0");
        const tag = card?.querySelector("em")?.textContent.trim() || "断点";
    
        node.setAttribute("role", "button");
        node.setAttribute("tabindex", "0");
        node.setAttribute("aria-label", `切换到 ${step} ${tag}`);
    
        node.addEventListener("click", () => {
          setActiveFault(index);
          startFaultAutoplay();
        });
    
        node.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          setActiveFault(index);
          startFaultAutoplay();
        });
      });
    
      reducedMotion.addEventListener("change", syncFaultAutoplay);
      window.setActiveFault = setActiveFault;
    }
    
    const intakeForm = isHomePage ? document.querySelector("[data-intake-form]") : null;
    const intakeResult = isHomePage ? document.querySelector("[data-intake-result]") : null;
    const scenarioRequestSummary = isHomePage ? document.querySelector("[data-scenario-request-summary]") : null;
    const customSelects = isHomePage ? Array.from(document.querySelectorAll("[data-custom-select]")) : [];
    
    if (customSelects.length) {
      customSelects.forEach(initCustomSelect);
    
      document.addEventListener("click", (event) => {
        customSelects.forEach((select) => {
          if (!select.contains(event.target)) setCustomSelectOpen(select, false);
        });
      });

      window.addEventListener("resize", () => {
        customSelects.forEach((select) => {
          if (select.classList.contains("is-open")) positionCustomSelectMenu(select);
        });
      }, { passive: true });
    }
    
    if (intakeForm) {
      const scenarioInputs = intakeForm.querySelectorAll('input[name="scene"]');
    
      scenarioInputs.forEach((input) => {
        input.addEventListener("change", () => {
          setScenarioRequestMessage("不需要写完整方案，先把真实问题留下来就好。");
        });
      });
    
      intakeForm.addEventListener("reset", () => {
        window.requestAnimationFrame(() => customSelects.forEach(syncCustomSelect));
      });
    
      intakeForm.addEventListener("submit", async (event) => {
        event.preventDefault();
    
        const formData = new FormData(intakeForm);
        const submitButton = intakeForm.querySelector('button[type="submit"]');
        submitButton?.setAttribute("disabled", "disabled");
        intakeForm.setAttribute("aria-busy", "true");
        setScenarioRequestMessage("正在发送需求场景...", "pending");
    
        try {
          const scenarioRequest = await createScenarioRequest(scenarioRequestDataFromFormData(formData));
          const scene = scenarioRequest.scene || clean(formData.get("scene")) || "未选择卡点类型";
          intakeForm.reset();
          setScenarioRequestMessage(`已收到：${scene}。我们会按你留下的联系方式继续沟通。`, "success");
        } catch (error) {
          setScenarioRequestMessage(error.message || "需求场景发送失败，请稍后再试。", "error");
        } finally {
          submitButton?.removeAttribute("disabled");
          intakeForm.removeAttribute("aria-busy");
        }
      });
    }

    function initCustomSelect(select) {
      const trigger = select.querySelector("[data-custom-select-trigger]");
      const options = customSelectOptions(select);
    
      syncCustomSelect(select);
    
      trigger?.addEventListener("click", () => {
        setCustomSelectOpen(select, !select.classList.contains("is-open"), true);
      });
    
      trigger?.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
        event.preventDefault();
        setCustomSelectOpen(select, true, true, event.key === "ArrowUp" ? "last" : "selected");
      });
    
      options.forEach((option, index) => {
        option.addEventListener("click", () => chooseCustomSelectOption(select, option));
    
        option.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            chooseCustomSelectOption(select, option);
            return;
          }
    
          if (event.key === "Escape") {
            event.preventDefault();
            setCustomSelectOpen(select, false);
            trigger?.focus();
            return;
          }
    
          if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
          event.preventDefault();
          const nextIndex = event.key === "ArrowDown"
            ? Math.min(index + 1, options.length - 1)
            : Math.max(index - 1, 0);
          options[nextIndex]?.focus();
        });
      });
    }

    function chooseCustomSelectOption(select, option) {
      const input = customSelectInput(select);
      if (input) {
        input.value = option.dataset.value || option.textContent.trim();
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    
      syncCustomSelect(select);
      setCustomSelectOpen(select, false);
      select.querySelector("[data-custom-select-trigger]")?.focus();
    }

    function syncCustomSelect(select) {
      const input = customSelectInput(select);
      const label = select.querySelector("[data-custom-select-label]");
      const options = customSelectOptions(select);
      const selectedOption = options.find((option) => option.dataset.value === input?.value) || options[0];
      if (!selectedOption || !input) return;
    
      input.value = selectedOption.dataset.value || selectedOption.textContent.trim();
      if (label) label.textContent = selectedOption.textContent.trim();
    
      options.forEach((option) => {
        const selected = option === selectedOption;
        option.classList.toggle("is-selected", selected);
        option.setAttribute("aria-selected", String(selected));
      });
    }

    function setCustomSelectOpen(select, open, focusOption = false, focusTarget = "selected") {
      const trigger = select.querySelector("[data-custom-select-trigger]");
      if (open) {
        positionCustomSelectMenu(select);
      } else {
        resetCustomSelectMenuPlacement(select);
      }
      select.classList.toggle("is-open", open);
      trigger?.setAttribute("aria-expanded", String(open));
    
      if (!open || !focusOption) return;
    
      window.requestAnimationFrame(() => {
        const options = customSelectOptions(select);
        const target = focusTarget === "last"
          ? options.at(-1)
          : options.find((option) => option.classList.contains("is-selected")) || options[0];
        target?.focus();
      });
    }

    function positionCustomSelectMenu(select) {
      const trigger = select.querySelector("[data-custom-select-trigger]");
      const menu = select.querySelector("[data-custom-select-menu]");
      const container = select.closest(".intake-form");
      if (!trigger || !menu) return;

      const triggerRect = trigger.getBoundingClientRect();
      const containerRect = container?.getBoundingClientRect();
      const placement = customSelectMenuPlacement({
        triggerTop: triggerRect.top,
        triggerBottom: triggerRect.bottom,
        menuHeight: customSelectMenuHeight(select, menu),
        viewportHeight: window.innerHeight,
        containerTop: containerRect?.top,
        containerBottom: containerRect?.bottom,
        preferredPlacement: select.dataset.customSelectPlacement,
      });

      select.classList.toggle("is-upward", placement.upward);
      select.style.setProperty("--custom-select-menu-max-height", `${Math.round(placement.maxHeight)}px`);
    }

    function resetCustomSelectMenuPlacement(select) {
      select.classList.remove("is-upward");
      select.style.removeProperty("--custom-select-menu-max-height");
    }

    function customSelectMenuHeight(select, menu) {
      const optionCount = customSelectOptions(select).length;
      return Math.max(menu.scrollHeight, optionCount * 44 + 12);
    }

    function customSelectInput(select) {
      return select.closest(".custom-select-field")?.querySelector("[data-custom-select-input]");
    }

    function customSelectOptions(select) {
      return Array.from(select.querySelectorAll("[data-value]"));
    }

    function setScenarioRequestMessage(message, state = "idle") {
      intakeResult?.classList.toggle("is-submitted", state === "success");
      intakeResult?.classList.toggle("is-error", state === "error");
      intakeResult?.classList.toggle("is-pending", state === "pending");
      if (scenarioRequestSummary) scenarioRequestSummary.textContent = message;
    }
    
    function clean(value) {
      return value?.toString().trim();
    }
    
    function refreshFaultDiagnosisTitle() {
      if (!faultCards.length) return;
    
      const activeCard = faultCards[activeFaultIndex];
      const title = activeCard?.dataset.panelTitle || activeCard?.querySelector("h3")?.textContent.trim();
      renderFaultTitle(title);
    }
    
    function refreshSmartTitles() {
      smartWrapAllTitles();
      refreshFaultDiagnosisTitle();
    }
    
    function runWhenIdle(callback, timeout = 800) {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(callback, { timeout });
        return;
      }
    
      window.setTimeout(callback, 80);
    }
    
    const hasSmartTitleWork = !isCaseDetailPage || document.querySelector(".case-detail-label h2, [data-case-title], [data-diagnosis-title]");
    
    if (hasSmartTitleWork) {
      if (isCaseDetailPage) {
        window.requestAnimationFrame(() => runWhenIdle(refreshSmartTitles));
      } else {
        refreshSmartTitles();
      }
    
      if (document.fonts?.ready) {
        document.fonts.ready
          .then(() => {
            if (isCaseDetailPage) {
              runWhenIdle(refreshSmartTitles, 1200);
              return;
            }
    
            refreshSmartTitles();
          })
          .catch(() => {});
      }
      window.addEventListener("resize", scheduleSmartTitleWrap, { passive: true });
    }
  } finally {
    scope.restore()
  }

  return () => scope.cleanup()
}
